// Unpublished Work © 2022-2024 Deere & Company.

import React from 'react';
import {IconDotFilled} from '@deere/icons';
import StatusText from 'OnLabor/workboard/common/status-text';
import {getOvertimeStatus} from 'OnLabor/workboard/utils/overtime-status';
import {replaceTranslationNames} from 'Utils/translation-utils';
import {formatMinutesToHours} from 'Utils/time-utils';
import {groupBy, orderBy} from 'lodash';
import {VALID_HEX_COLORS} from 'Common/constants/valid-hex-color';

const NONE_COLOR = 'none';

function createRoleNameMap(userRoles) {
    return new Map(userRoles.map((userRole) => [userRole.userRoleId, userRole.title]));
}

function hasPhoneNumer(op) {
    if (op.phoneNumber && op.phoneNumber.phoneNumber && op.phoneNumber.phoneNumber !== '') {
        return true;
    }
    if (op.authenticatedUser) {
        if (op.authenticatedUser.phoneNumber && op.authenticatedUser.phoneNumber.phoneNumber && op.authenticatedUser.phoneNumber.phoneNumber !== '') {
            return true;
        }
        return false;
    }
    return false;
}

function sortMembers(a, b) {
    const aTitle = a.title || '';
    const bTitle = b.title || '';

    if (aTitle < bTitle) {
        return -1;
    }
    if (aTitle > bTitle) {
        return 1;
    }

    return 0;
}

function getOperatorFormattedName(operator) {
    return `${operator.firstName} ${operator.lastName}`.trim();
}

function getFormattedOperators({
    enablePhoneCheck,
    operators,
    overtimeReachedThreshold,
    overtimeWarningThreshold,
    showIcon,
    translations
}, hideExtraContent = false) {
    const ICON_STYLE = {
        style: {
            fontSize: '12px'
        }
    };

    return operators?.map((operator) => {
        const formattedOperator = {
            extraContent: hideExtraContent ? null : (
                <StatusText
                    dangerColor='#f00'
                    status={getOvertimeStatus(operator, overtimeReachedThreshold, overtimeWarningThreshold)}
                    warningColor='#ff9600'
                >
                    {
                        operator.unavailableForSelection && !operator.inactive ? (
                            <div className='deleted-jobs-category'>{translations.ONLINK_OPERATOR_UNAVAILABLE}</div>
                        ) : null
                    }
                    {
                        operator.inactive ? (
                            <div>{translations.inactive}</div>
                        ) : null
                    }
                    {
                        operator && !operator.deleted && operator.minutesWorkedForWeek ?
                            replaceTranslationNames(translations.ONLINK_HOURS_THIS_WEEK, {
                                '0': formatMinutesToHours(operator.minutesWorkedForWeek)
                            }) : null
                    }
                </StatusText>
            ),
            icon: !showIcon ? null : (
                <IconDotFilled
                    primary={operator?.onLinkUser?.groupColor}
                    {...ICON_STYLE}
                />
            ),
            id: operator.id,
            subtitle: operator.onTimeOff ? translations.NOT_AVAILABLE : '',
            title: getOperatorFormattedName(operator),
            equipmentTypes: operator?.onLinkUser?.assignableEquipment || []
        };

        if (enablePhoneCheck && !hasPhoneNumer(operator)) {
            formattedOperator.disabled = true;
            if (showIcon) {
                formattedOperator.icon = (
                    <IconDotFilled
                        primary={'#C7C6C5'}
                        size={'sm'}
                    />
                );
            }
        }

        return formattedOperator;
    }).sort(sortMembers);
}

function getGroupedFormattedOperators({
    enablePhoneCheck,
    operators,
    overtimeReachedThreshold,
    overtimeWarningThreshold,
    showIcon,
    translations
}, hideExtraContent = false) {
    const {
        [NONE_COLOR]: unassignedOperators,
        ...operatorsByColor
    } = groupBy(operators, (operator) => operator?.onLinkUser?.groupColor || NONE_COLOR);

    const groupedOperators = orderBy(Object.keys(operatorsByColor).map((color) => ({
        Icon: (
            <IconDotFilled
                primary={color}
            />
        ),
        id: color,
        title: translations[VALID_HEX_COLORS.find(({id}) => id === color)?.translationKeyWord],
        items: getFormattedOperators({
            enablePhoneCheck,
            operators: operatorsByColor[color],
            overtimeReachedThreshold,
            overtimeWarningThreshold,
            showIcon,
            translations
        }, hideExtraContent)
    })), 'title');

    if (unassignedOperators) {
        groupedOperators.push({
            id: NONE_COLOR,
            title: translations.ONLINK_OPERATOR_UNGROUPED,
            items: getFormattedOperators({
                enablePhoneCheck,
                operators: unassignedOperators,
                overtimeReachedThreshold,
                overtimeWarningThreshold,
                showIcon,
                translations
            }, hideExtraContent)
        });
    }

    return {
        groupedOperators
    };
}

export {
    createRoleNameMap,
    getGroupedFormattedOperators,
    getFormattedOperators,
    getOperatorFormattedName
};
